import React, { useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/common/PageHeader";
import { Sparkles, Send, RefreshCw, Flame, ShieldAlert, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function Bubble({ role, children, testId }) {
  const isUser = role === "user";
  return (
    <div
      className={cn("flex w-full mb-3 animate-pop-in", isUser ? "justify-end" : "justify-start")}
      data-testid={testId}
    >
      <div
        className={cn(
          "max-w-[85%] md:max-w-[70%] px-4 py-3 rounded-2xl leading-relaxed",
          isUser
            ? "bg-sage-100 text-ink-900 rounded-br-sm"
            : "bg-white shadow-card text-ink-900 rounded-bl-sm"
        )}
      >
        <div className="whitespace-pre-wrap text-[15px]">{children}</div>
      </div>
    </div>
  );
}

export default function Companion() {
  const t = useT();
  const { lang } = useI18n();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("companion"); // companion|craving
  const [slipping, setSlipping] = useState(false);
  const [loadingHist, setLoadingHist] = useState(true);
  const scrollRef = useRef(null);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  useEffect(() => { scrollToBottom(); }, [messages.length]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingHist(true);
      try {
        const r = await api.get("/chat/history");
        if (!alive) return;
        const arr = Array.isArray(r.data) ? r.data : (r.data?.messages || r.data?.items || []);
        // Normalize
        const norm = arr.map((m, i) => ({
          id: m.id || i,
          role: (m.role === "user" || m.from === "user" || m.author === "user") ? "user" : "assistant",
          text: m.text || m.content || m.message || "",
        }));
        setMessages(norm);
      } catch { /* ignore */ }
      finally { if (alive) setLoadingHist(false); }
    })();
    return () => { alive = false; };
  }, []);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    const localId = `u-${Date.now()}`;
    setMessages((prev) => [...prev, { id: localId, role: "user", text }]);
    setBusy(true);
    try {
      const r = await api.post("/chat", { text, mode, slipping });
      const reply = r.data?.reply || r.data?.text || r.data?.message || r.data?.content || "";
      setMessages((prev) => [...prev, { id: `a-${Date.now()}`, role: "assistant", text: reply || "…" }]);
      if (r.data?.crisis || r.data?.high_risk) {
        toast.warning(t("client.ifCrisis"));
      }
    } catch (e) {
      const msg = errMsg(e, t("errors.generic"));
      setMessages((prev) => [...prev, { id: `err-${Date.now()}`, role: "assistant", text: msg }]);
    } finally {
      setBusy(false);
    }
  };

  const newConversation = async () => {
    try {
      await api.post("/chat/new");
    } catch {}
    setMessages([]);
    toast.success(t("common.saved"));
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("nav.companion")}
        title={t("client.startCompanion")}
        subtitle={t("brand.tagline")}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMode(mode === "companion" ? "craving" : "companion")}
              data-testid="companion-toggle-mode"
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium border transition",
                mode === "craving"
                  ? "bg-clay-500 text-white border-clay-500 hover:bg-clay-600"
                  : "bg-white border-border text-ink-700 hover:text-ink-900"
              )}
            >
              <Flame className="h-3.5 w-3.5" /> {mode === "craving" ? t("client.endCraving") : t("client.startCraving")}
            </button>
            <button
              onClick={() => setSlipping((v) => !v)}
              data-testid="companion-toggle-slipping"
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium border transition",
                slipping
                  ? "bg-rust-500 text-white border-rust-500 hover:bg-rust-600"
                  : "bg-white border-border text-ink-700 hover:text-ink-900"
              )}
            >
              <ShieldAlert className="h-3.5 w-3.5" />
              {t("client.slippingHelp")}
            </button>
            <button
              onClick={newConversation}
              data-testid="companion-new"
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium border border-border bg-white text-ink-700 hover:text-ink-900"
            >
              <RefreshCw className="h-3.5 w-3.5" /> {t("common.retry")}
            </button>
          </div>
        }
      />

      <div className="rounded-3xl bg-cream-50 border border-border p-4 md:p-6 flex flex-col h-[62vh] min-h-[440px]" data-testid="companion-shell">
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto pr-1"
          data-testid="companion-messages"
        >
          {loadingHist && (
            <div className="text-center text-sm text-ink-500 py-6">{t("common.loading")}</div>
          )}
          {!loadingHist && messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center px-6 py-10">
              <div className="h-14 w-14 rounded-2xl bg-white shadow-card flex items-center justify-center mb-4">
                <Sparkles className="h-6 w-6 text-sage-500" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-2xl text-ink-900 tracking-tight">{t("client.greeting")}</h3>
              <p className="text-ink-500 mt-2 max-w-md">{t("brand.tagline")}</p>
            </div>
          )}
          {messages.map((m) => (
            <Bubble key={m.id} role={m.role} testId={`companion-msg-${m.role}`}>{m.text}</Bubble>
          ))}
          {busy && (
            <div className="flex gap-1 items-center text-ink-500 text-sm mt-2" data-testid="companion-typing">
              <span className="h-1.5 w-1.5 rounded-full bg-sage-500 animate-pulse" />
              <span className="h-1.5 w-1.5 rounded-full bg-sage-500 animate-pulse [animation-delay:120ms]" />
              <span className="h-1.5 w-1.5 rounded-full bg-sage-500 animate-pulse [animation-delay:240ms]" />
            </div>
          )}
        </div>

        <div className="mt-4 rounded-2xl bg-white shadow-card p-2 flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            data-testid="companion-input"
            rows={1}
            placeholder={t("client.typeMessage")}
            className="flex-1 resize-none bg-transparent outline-none text-[15px] px-3 py-2 max-h-40 min-h-[42px]"
          />
          <button
            onClick={sendMessage}
            disabled={busy || !input.trim()}
            data-testid="companion-send"
            className="inline-flex items-center gap-1.5 rounded-full bg-sage-500 hover:bg-sage-600 disabled:opacity-40 text-white px-4 py-2 text-sm font-medium"
          >
            <Send className="h-4 w-4" strokeWidth={1.75} />
            {t("common.send")}
          </button>
        </div>

        <div className="mt-2 text-[11px] text-ink-500 flex items-center gap-1">
          <LifeBuoy className="h-3 w-3" />
          {lang === "nl" ? "In geval van nood, ga naar Veiligheid." : "In an emergency, visit Safety."}
        </div>
      </div>
    </div>
  );
}
